import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ImageGenerator from "./ImageGenerator";
import axios from "axios";
import IngredientSelection from "./IngredientSelection";
import { playAudio } from "../utils/audioManager";

function FinalOutcome({ selectedTable, selectedIngredients, onGoBack }) {
  const navigate = useNavigate();
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [description, setDescription] = useState("");

  useEffect(() => {
    console.log("Current generatedImageUrl:", generatedImageUrl);
  }, [generatedImageUrl]);

  const generateContent = useCallback(async () => {
    if (isGenerating) return;

    setIsGenerating(true);
    setError(null);

    const newDescription = `Create a photorealistic ${
      selectedTable.name
    } dining scene with traditional ${
      selectedTable.id
    } tableware and decorations. The scene should feature authentic ${
      selectedTable.id
    } dishes made with: ${selectedIngredients.map(
      (ing) => ing.name
    )}. The lighting should be warm and inviting, highlighting the textures and colors of the food. The composition should be from a slightly elevated angle to showcase the entire spread.`;
    setDescription(newDescription);

    let retryCount = 0;
    const maxRetries = 3;

    while (retryCount < maxRetries) {
      try {
        const imageUrl = await ImageGenerator(newDescription);
        if (imageUrl) {
          setGeneratedImageUrl(imageUrl);
          playAudio("/sound/ThankYou.MP3", 1);
          break;
        }
        throw new Error("No image URL returned");
      } catch (error) {
        console.error(`Attempt ${retryCount + 1} failed:`, error);
        retryCount++;

        if (retryCount === maxRetries) {
          setError(
            `We're sorry, but we couldn't generate an image after ${maxRetries} attempts. Please try again later.`
          );
        }
        // Wait 2 seconds before retrying
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
    setIsGenerating(false);
  }, [selectedTable, selectedIngredients]);

  const LoadingMessage = () => {
    const [dots, setDots] = useState("");
    const [messageIndex, setMessageIndex] = useState(0);
    const messages = [
      "Creating your masterpiece",
      "Arranging the table settings",
      "Adding finishing touches",
      "Adjusting the lighting",
      "Almost ready to serve",
    ];

    useEffect(() => {
      const dotInterval = setInterval(() => {
        setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
      }, 500);

      const messageInterval = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
      }, 4000);

      return () => {
        clearInterval(dotInterval);
        clearInterval(messageInterval);
      };
    }, []);

    return (
      <div className="text-center">
        <div className="animate-pulse mb-4">
          <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p className="text-xl text-blue-700">
          {messages[messageIndex]}
          {dots}
        </p>
        <p className="text-sm text-gray-600 mt-2">
          This usually takes about 10-20 seconds
        </p>
      </div>
    );
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(generatedImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cultural-cuisine-${selectedTable.id}-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  return (
    <div className="p-8 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => onGoBack()}
          className="flex items-center gap-2 bg-white hover:bg-gray-100 text-blue-700 font-semibold py-2 px-4 rounded-lg shadow transition-all duration-200 hover:shadow-md"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to Home
        </button>
      </div>

      <h2 className="text-4xl font-bold mb-4 text-center text-blue-800">
        Your Cultural Cuisine
      </h2>
      <div className="mb-6">
        <h3 className="text-2xl font-semibold mb-2 text-blue-700">
          Selected Table:
        </h3>
        <p className="text-xl">{selectedTable.name}</p>
      </div>
      <div className="mb-6">
        <h3 className="text-2xl font-semibold mb-2 text-blue-700">
          Selected Ingredients:
        </h3>
        <ul className="list-disc list-inside">
          {selectedIngredients.map((ingredient) => (
            <li key={ingredient.id} className="text-xl">
              {ingredient.name}
            </li>
          ))}
        </ul>
      </div>

      {!generatedImageUrl && !isGenerating && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => generateContent()}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Generate My Cuisine Table
          </button>
        </div>
      )}

      {description && (
        <div className="mb-6">
          <h3 className="text-2xl font-semibold mb-2 text-blue-700">
            Description:
          </h3>
          <p className="text-xl">{description}</p>
        </div>
      )}

      {isGenerating ? (
        <LoadingMessage />
      ) : error ? (
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <p className="text-lg text-blue-600">
            Please try clicking "Generate Image" again - sometimes it works on
            the second try!
          </p>
          <button
            onClick={() => generateContent()}
            className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Generate Image Again
          </button>
        </div>
      ) : generatedImageUrl ? (
        <div className="mt-8">
          <div className="flex justify-end mb-4">
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2.5 px-5 rounded-lg shadow transition-all duration-200 hover:shadow-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Download Image
            </button>
          </div>
          <div className="rounded-lg overflow-hidden shadow-lg">
            <img
              src={generatedImageUrl}
              alt="Generated cuisine"
              className="w-full h-auto"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default FinalOutcome;
